// Phase 4: Enhanced State Monitoring and Logging

interface AuthStateLog {
  timestamp: number;
  userId?: string;
  event: string;
  details: any;
  source: string;
}

interface AuthStateViolation {
  timestamp: number;
  userId: string;
  violationType: 'company_mismatch' | 'missing_profile' | 'suspended_access' | 'corrupted_state';
  details: any;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

class AuthStateMonitor {
  private logs: AuthStateLog[] = [];
  private violations: AuthStateViolation[] = [];
  private readonly MAX_LOGS = 1000;
  private readonly MAX_VIOLATIONS = 500;

  logAuthEvent(event: string, details: any, source: string, userId?: string): void {
    const log: AuthStateLog = {
      timestamp: Date.now(),
      userId,
      event,
      details,
      source
    };

    this.logs.push(log);
    
    // Keep logs within limit
    if (this.logs.length > this.MAX_LOGS) {
      this.logs.splice(0, this.logs.length - this.MAX_LOGS);
    }

    console.log(`[AuthMonitor] ${source}: ${event}`, details);
  }

  reportViolation(
    userId: string,
    violationType: AuthStateViolation['violationType'],
    details: any,
    severity: AuthStateViolation['severity'] = 'medium'
  ): void {
    const violation: AuthStateViolation = {
      timestamp: Date.now(),
      userId,
      violationType,
      details,
      severity
    };

    this.violations.push(violation);
    
    // Keep violations within limit
    if (this.violations.length > this.MAX_VIOLATIONS) {
      this.violations.splice(0, this.violations.length - this.MAX_VIOLATIONS);
    }

    console.warn(`[AuthMonitor] VIOLATION (${severity}): ${violationType}`, details);

    // Send critical violations to backend (future enhancement)
    if (severity === 'critical') {
      this.sendCriticalAlert(violation);
    }
  }

  private async sendCriticalAlert(violation: AuthStateViolation): Promise<void> {
    try {
      // Future: Send to monitoring service or Supabase edge function
      console.error('[AuthMonitor] CRITICAL VIOLATION:', violation);
    } catch (error) {
      console.error('[AuthMonitor] Failed to send critical alert:', error);
    }
  }

  validateUserState(user: any, profile: any, companyMembers: any[]): boolean {
    if (!user || !profile) return true; // Skip validation if incomplete data

    let isValid = true;

    // Check company_id consistency
    if (profile.company_id) {
      const isMember = companyMembers.some(member => 
        member.user_id === user.id && member.company_id === profile.company_id
      );
      
      if (!isMember) {
        this.reportViolation(
          user.id,
          'company_mismatch',
          {
            profileCompanyId: profile.company_id,
            membershipExists: false,
            memberships: companyMembers.filter(m => m.user_id === user.id)
          },
          'high'
        );
        isValid = false;
      }
    }

    // Check suspended user access
    if (profile.status === 'suspended') {
      this.reportViolation(
        user.id,
        'suspended_access',
        {
          suspendedUntil: profile.suspended_until,
          suspensionReason: profile.suspension_reason
        },
        'critical'
      );
      isValid = false;
    }

    return isValid;
  }

  getRecentLogs(count: number = 50): AuthStateLog[] {
    return this.logs.slice(-count);
  }

  getRecentViolations(count: number = 20): AuthStateViolation[] {
    return this.violations.slice(-count);
  }

  getStatistics(): {
    totalLogs: number;
    totalViolations: number;
    violationsByType: Record<string, number>;
    violationsBySeverity: Record<string, number>;
  } {
    const violationsByType: Record<string, number> = {};
    const violationsBySeverity: Record<string, number> = {};

    this.violations.forEach(violation => {
      violationsByType[violation.violationType] = (violationsByType[violation.violationType] || 0) + 1;
      violationsBySeverity[violation.severity] = (violationsBySeverity[violation.severity] || 0) + 1;
    });

    return {
      totalLogs: this.logs.length,
      totalViolations: this.violations.length,
      violationsByType,
      violationsBySeverity
    };
  }

  exportLogs(): { logs: AuthStateLog[]; violations: AuthStateViolation[] } {
    return {
      logs: [...this.logs],
      violations: [...this.violations]
    };
  }

  clearLogs(): void {
    this.logs = [];
    this.violations = [];
    console.log('[AuthMonitor] Logs and violations cleared');
  }
}

// Singleton instance
export const authStateMonitor = new AuthStateMonitor();

// Helper functions
export const logAuthTransition = (
  event: string,
  userId?: string,
  details: any = {},
  source: string = 'unknown'
): void => {
  authStateMonitor.logAuthEvent(event, details, source, userId);
};

export const validateAndLogUserState = (user: any, profile: any, companyMembers: any[] = []): boolean => {
  const isValid = authStateMonitor.validateUserState(user, profile, companyMembers);
  
  logAuthTransition(
    'user_state_validation',
    user?.id,
    { isValid, profileCompanyId: profile?.company_id, membershipCount: companyMembers.length },
    'state_validator'
  );
  
  return isValid;
};
